import { useEffect, useRef } from 'react';
import { Composer } from './components/Composer.tsx';
import { FollowUps } from './components/FollowUps.tsx';
import { Header } from './components/Header.tsx';
import { MessageView } from './components/MessageView.tsx';
import { SimulatedClock } from './components/SimulatedClock.tsx';
import { Welcome } from './components/Welcome.tsx';
import { useAppContext } from './hooks/useAppContext.ts';
import { useChat } from './hooks/useChat.ts';
import { useTheme } from './hooks/useTheme.ts';
import { followUps, suggestions } from './lib/suggestions.ts';

export const DISCLAIMER =
  'Datos del Ayuntamiento de Sevilla. Información orientativa; los horarios pueden variar.';

export function App() {
  const ctx = useAppContext();
  const theme = useTheme(ctx?.now ?? null);
  const { messages, busy, send, stop, retry, clear } = useChat();
  const endRef = useRef<HTMLDivElement>(null);
  const last = messages.at(-1);

  // Follow the answer as it streams in, and the follow-ups that appear when it ends
  // (not on the welcome screen: its title must stay in sight)
  useEffect(() => {
    if (messages.length > 0) endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, last?.text, last?.status]);

  return (
    <div className="flex h-dvh flex-col">
      <Header
        theme={theme.mode}
        onTheme={theme.cycle}
        onNew={clear}
        canReset={messages.length > 0}
      />
      {ctx?.simulated && <SimulatedClock now={ctx.now} />}

      <main className="flex-1 overflow-y-auto overscroll-contain" aria-live="polite">
        <div className="mx-auto max-w-2xl space-y-4 px-4 py-4">
          {messages.length === 0 ? (
            <Welcome suggestions={suggestions(ctx)} onPick={(s) => void send(s)} />
          ) : (
            messages.map((m) => (
              <MessageView
                key={m.id}
                message={m}
                onRetry={m === last && m.status === 'error' ? retry : undefined}
              />
            ))
          )}
          {last?.status === 'done' && last.brotherhood && (
            <FollowUps
              name={last.brotherhood.name}
              questions={followUps(last.brotherhood, {
                ctx,
                tools: last.tools,
                asked: messages.filter((m) => m.role === 'user').map((m) => m.text),
              })}
              onPick={(q) => void send(q)}
            />
          )}
          <div ref={endRef} />
        </div>
      </main>

      <div className="border-t border-purple/10 bg-cream-200/80 pb-[max(env(safe-area-inset-bottom),0.25rem)] backdrop-blur-sm dark:border-cream/10 dark:bg-night-800/95">
        <Composer busy={busy} onSend={(t) => void send(t)} onStop={stop} />
        <p className="mx-auto max-w-2xl px-4 pb-1.5 text-center text-[0.72rem] leading-snug text-ink-soft dark:text-cream/65">
          {DISCLAIMER}
        </p>
      </div>
    </div>
  );
}
